import React, { useState } from "react";
import { X, Share2, Loader2, Copy, Check, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { createShare } from "@/lib/forgeApi";

/**
 * Share-strategy modal.
 *
 * Posts the current built strategy snapshot to /api/forge/share, then shows
 * the public URL with a one-click copy. Optional title + note fields are
 * sanitized server-side (140 / 500 char caps).
 */
const ShareModal = ({ onClose, strategy, ticker }) => {
  const [title, setTitle] = useState(
    strategy?.template?.name && ticker ? `${ticker} · ${strategy.template.name}` : "",
  );
  const [note, setNote] = useState("");
  const [creating, setCreating] = useState(false);
  const [share, setShare] = useState(null);
  const [copied, setCopied] = useState(false);

  const handleCreate = async () => {
    if (!strategy?.metrics?.legs?.length) {
      toast.error("Build a strategy first");
      return;
    }
    setCreating(true);
    try {
      const res = await createShare({
        symbol: strategy?.pricing?.symbol || ticker,
        template_id: strategy?.template?.id,
        template_name: strategy?.template?.name,
        title: title || null,
        note: note || null,
        // Lean snapshot — store only what the read-only page needs to render.
        snapshot: {
          template: strategy?.template,
          inputs: strategy?.inputs,
          pricing: strategy?.pricing,
          payoff_curve: strategy?.payoff_curve,
          earnings: strategy?.earnings,
          metrics: {
            net_premium: strategy?.metrics?.net_premium,
            max_profit: strategy?.metrics?.max_profit,
            max_loss: strategy?.metrics?.max_loss,
            pop: strategy?.metrics?.pop,
            breakevens: strategy?.metrics?.breakevens,
            greeks: strategy?.metrics?.greeks,
            spot: strategy?.metrics?.spot,
            cone: strategy?.metrics?.cone,
            today_curve: strategy?.metrics?.today_curve,
            legs: strategy?.metrics?.legs,
          },
        },
      });
      setShare(res);
      toast.success("Share link created");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Share failed");
    } finally {
      setCreating(false);
    }
  };

  const fullUrl = share ? `${window.location.origin}${share.share_url}` : "";

  const handleCopy = async () => {
    // Try the modern Clipboard API first; fall back to a select+execCommand
    // dance for restrictive contexts (preview iframes, http origins, etc).
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(fullUrl);
      } else {
        throw new Error("no-clipboard");
      }
      setCopied(true);
      toast.success("Copied to clipboard");
      setTimeout(() => setCopied(false), 1800);
    } catch {
      try {
        const ta = document.createElement("textarea");
        ta.value = fullUrl;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
        setCopied(true);
        toast.success("Copied to clipboard");
        setTimeout(() => setCopied(false), 1800);
      } catch {
        toast.error("Copy blocked — select the URL manually");
      }
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] bg-[#050505]/85 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid="share-modal"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="bg-[#0a0a0c] border border-[#00E5FF]/30 rounded-2xl p-5 sm:p-6 w-full max-w-md">
        <div className="flex items-start justify-between gap-3 mb-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.24em] text-[#00E5FF] flex items-center gap-1.5">
              <Share2 className="w-3 h-3" /> Share strategy
            </div>
            <h3 className="text-lg font-medium tracking-tight mt-0.5">
              Generate a public link
            </h3>
            <p className="text-[12px] text-white/45 mt-1 leading-relaxed">
              Anyone with the link can view your trade — read-only, no login required.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="share-close"
            className="p-1 text-white/55 hover:text-white border border-white/15 rounded"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {!share ? (
          <>
            <div className="space-y-3 mb-4">
              <label className="block">
                <span className="text-[10px] uppercase tracking-[0.18em] text-white/45">
                  Title (optional)
                </span>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  maxLength={140}
                  data-testid="share-title-input"
                  placeholder="e.g. SPY July Iron Condor"
                  className="mt-1 w-full bg-[#050505] border border-white/10 focus:border-[#00E5FF]/60 outline-none rounded-md px-3 py-2 text-[13px] text-white placeholder-white/25"
                />
              </label>
              <label className="block">
                <span className="text-[10px] uppercase tracking-[0.18em] text-white/45">
                  Note (optional, 500 chars)
                </span>
                <textarea
                  value={note}
                  onChange={(e) => setNote(e.target.value.slice(0, 500))}
                  data-testid="share-note-input"
                  rows={3}
                  placeholder="Why this trade right now?"
                  className="mt-1 w-full bg-[#050505] border border-white/10 focus:border-[#00E5FF]/60 outline-none rounded-md px-3 py-2 text-[12.5px] text-white placeholder-white/25 resize-none"
                />
              </label>
            </div>
            <button
              type="button"
              onClick={handleCreate}
              disabled={creating || !strategy?.metrics?.legs?.length}
              data-testid="share-create"
              className="w-full inline-flex items-center justify-center gap-1.5 bg-[#00E5FF] text-black hover:bg-[#7FF5FF] px-4 py-2 rounded-md text-[12.5px] font-medium disabled:opacity-40"
            >
              {creating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Share2 className="w-3.5 h-3.5" />}
              {creating ? "Generating…" : "Generate share link"}
            </button>
          </>
        ) : (
          <div className="space-y-3" data-testid="share-result">
            <div>
              <span className="text-[10px] uppercase tracking-[0.18em] text-white/45">
                Your link
              </span>
              <div className="mt-1 flex items-center gap-1.5">
                <input
                  readOnly
                  value={fullUrl}
                  data-testid="share-url-input"
                  className="flex-1 bg-[#050505] border border-[#00E5FF]/30 rounded-md px-3 py-2 text-[11.5px] text-[#00E5FF] num"
                  onFocus={(e) => e.target.select()}
                />
                <button
                  type="button"
                  onClick={handleCopy}
                  data-testid="share-copy"
                  className="p-2 border border-[#00E5FF]/40 rounded-md text-[#00E5FF] hover:bg-[#00E5FF]/10 transition"
                  title="Copy URL"
                >
                  {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                </button>
                <a
                  href={share.share_url}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="share-open"
                  className="p-2 border border-white/15 rounded-md text-white/70 hover:text-white hover:border-white/35 transition"
                  title="Open in new tab"
                >
                  <ExternalLink className="w-3.5 h-3.5" />
                </a>
              </div>
            </div>
            <p className="text-[11px] text-white/45 leading-relaxed">
              The snapshot is frozen — sliders or new builds on your side
              won't change what visitors see. Share away.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ShareModal;
